import React, { useEffect, useRef, useState } from "react";
import { styled } from "../stitches.config";
import { useUpdateMyPresence } from "@liveblocks/react";
import { useAtom } from "jotai";
import { useUpdateAtom } from "jotai/utils";
import { useSendAnalyticsEvent } from "../analytics/events";
import { defaultBlockCreate } from "../blocks/MenuBlock";
import Icon, { IconType } from "../components/Icon";
import {
  blockYouJustCreatedAtom,
  currentUserAtom,
  spaceMenuOpenAtom,
} from "../jotai/atoms";
import { useFeatureFlags } from "../liveblocks/maps";
import { useUpdatePresence } from "../liveblocks/presences";
import { AnyBlock, BlockType, Position, Size } from "../types";
import { pixelToGridPosition } from "../utils/gridPosition";
import { remToPixels } from "./Minimap";
import { ListMenu, ListMenuIconItem } from "../components/Menu";

export function SpaceContextMenu({
  addBlock,
}: {
  addBlock: (block: Partial<AnyBlock>, raise: boolean) => void;
}) {
  const lastMenuPos = useRef<Position>({ x: -9999, y: -9999 });
  const [menuOpen, setMenuOpen] = useAtom(spaceMenuOpenAtom);
  const [hoveredItem, setHoveredItem] = useState(0);

  const updatePresence = useUpdatePresence();
  const sendAnalyticsEvent = useSendAnalyticsEvent();

  useEffect(() => {
    if (!menuOpen) {
      updatePresence({ contextMenu: null });
    } else {
      updatePresence({
        contextMenu: {
          type: "space",
          position: {
            x: menuOpen.x + remToPixels(-1.2),
            y: menuOpen.y - remToPixels(0.95),
          },
          hoverIndex: hoveredItem,
        },
      });
    }
  }, [hoveredItem, menuOpen, updatePresence]);

  const setBlockYouJustCreated = useUpdateAtom(blockYouJustCreatedAtom);
  const updateMyPresence = useUpdateMyPresence();

  const [currentUser] = useAtom(currentUserAtom);

  function createBlock(type: BlockType) {
    if (type === "draw") {
      updateMyPresence({ drawMode: [[]] });

      return;
    }
    if (!menuOpen) return;

    const newBlock: Partial<AnyBlock> = {
      position: pixelToGridPosition(menuOpen),
      size: BLOCK_DEFAULT_SIZES[type] as Size,
      ...defaultBlockCreate(type, currentUser),
    };

    // Choosing a block type here is the equivalent of creating a new block for analytics
    if (newBlock.type) {
      sendAnalyticsEvent({ name: "blockCreated", type: newBlock.type });
    }

    addBlock(newBlock, true);
    setBlockYouJustCreated(newBlock.id);
  }

  const [featureFlags] = useFeatureFlags();

  useEffect(() => {
    if (menuOpen) {
      setHoveredItem(0);
      lastMenuPos.current = menuOpen;
    }
  }, [menuOpen]);

  // needed for fade-out animation
  const pos = menuOpen || lastMenuPos.current;
  const availableBlockTypes = BLOCK_MENU_ITEMS.filter(
    (t: string) => t !== "web" || featureFlags?.webBlock
  );

  return (
    <HoverRegion
      onMouseLeave={() => {
        setMenuOpen(null);
      }}
      on={!!menuOpen}
      style={{
        left: pos.x - remToPixels(8),
        top: pos.y - remToPixels(1),
      }}
    >
      <ListMenu
        data-cursor={"pointer"}
        onMouseLeave={() => {
          setHoveredItem(-1);
        }}
      >
        {availableBlockTypes.map((type, i) => (
          <ListMenuIconItem
            data-cursor={"pointer"}
            key={type}
            hover={hoveredItem == i}
            onMouseDown={(e: React.MouseEvent) => {
              // Prevent from triggering preview block
              e.stopPropagation();
            }}
            onClick={() => {
              createBlock(type);
              setMenuOpen(null);
            }}
            onMouseOver={() => setHoveredItem(i)}
          >
            <Icon style={iconStyle} icon={BLOCK_TYPE_ICONS[type]} />
            {BLOCK_TYPE_LABELS[type]}
          </ListMenuIconItem>
        ))}
      </ListMenu>
    </HoverRegion>
  );
}

const BLOCK_DEFAULT_SIZES = {
  "text/json": { width: 2, height: 2 },
  face: { width: 6, height: 8 },
  image: { width: 10, height: 10 },

  css: { width: 16, height: 12 },

  screen: { width: 16, height: 16 },
  imageSearch: { width: 20, height: 16 },
  youtube: { width: 20, height: 16 },

  web: { width: 20, height: 16 },

  //shouldn't happen:
  svg: { width: 1, height: 1 },
  link: { width: 1, height: 1 },
  text: { width: 1, height: 1 },
  menu: { width: 1, height: 1 },
};

export const iconStyle = {
  width: 24,
  height: 24,
  marginRight: 4,
};

export const BLOCK_MENU_ITEMS = [
  "text/json",
  "draw",
  "imageSearch",
  "face",
  "screen",
  "image",
  "youtube",
  "css",
  "web",
] as const;

export const BLOCK_TYPE_ICONS: Record<
  typeof BLOCK_MENU_ITEMS[number],
  IconType
> = {
  "text/json": "TextBlock",
  draw: "DrawBlock",
  imageSearch: "ImageSearch",
  face: "FaceDock",
  screen: "ScreenBlock",
  image: "ImgBlock",
  youtube: "Video",
  css: "Brush",
  web: "WebBlock",
};

export const BLOCK_TYPE_LABELS: Record<string, string> = {
  face: "Face Dock",
  web: "Browse Web [alpha]",
  text: "Write Text",
  "text/json": "Write Text",
  link: "Link", // not used
  draw: "Draw",
  image: "Upload Image",
  imageSearch: "Search for GIFs, Images",
  css: "Write Style Rules",
  screen: "Share Screen",
  youtube: "Watch YouTube Video",
};

const HoverRegion = styled("div", {
  position: "absolute",
  zIndex: "$contextMenu",
  margin: "-1rem",
  border: "1rem solid transparent",
  transformOrigin: "top center",
  transition: "transform 80ms ease-in-out, opacity 100ms ease-in-out",
  variants: {
    on: {
      true: {
        transform: "scale(1.0)",
        pointerEvents: "all",
        opacity: 1,
      },
      false: {
        transform: "scale(0.95)",
        pointerEvents: "none",
        opacity: 0,
      },
    },
  },
});
